import { computePosition, flip, shift } from "@floating-ui/dom";
import type { Editor } from "@tiptap/react";
import { posToDOMRect, ReactRenderer } from "@tiptap/react";

import { TemplateFormat } from "@/components/Playground2/types";
import MentionList, { type MentionListHandle } from "./MentionList";

interface MentionProps {
  query: string;
  editor: Editor;
  clientRect?: (() => DOMRect | null) | DOMRect | null;
  event?: KeyboardEvent;
}

interface SuggestionKeyDownProps {
  clientRect?: (() => DOMRect | null) | DOMRect | null;
  event?: KeyboardEvent;
}

const updatePosition = (editor: Editor, element: HTMLElement): void => {
  const virtualElement = {
    getBoundingClientRect: () =>
      posToDOMRect(
        editor.view,
        editor.state.selection.from,
        editor.state.selection.to,
      ),
  };

  computePosition(virtualElement, element, {
    placement: "bottom-start",
    strategy: "absolute",
    middleware: [shift(), flip()],
  }).then(({ x, y, strategy }) => {
    element.style.width = "max-content";
    element.style.position = strategy;
    element.style.left = `${x}px`;
    element.style.top = `${y}px`;
    element.style.zIndex = "9999";
  });
};

const mentionSuggestion = (
  getOptions: () => string[],
  mode: TemplateFormat = "f-string",
) => ({
  items: ({ query }: { query: string }) => {
    return getOptions().filter((option) => option.startsWith(query));
  },

  render: () => {
    let component: ReactRenderer<MentionListHandle> | undefined;

    return {
      onStart: (props: MentionProps) => {
        component = new ReactRenderer(MentionList, {
          props: { ...props, mode, query: props.query },
          editor: props.editor,
        });

        const clientRect =
          typeof props.clientRect === "function"
            ? props.clientRect()
            : props.clientRect;

        if (!clientRect) {
          return;
        }

        (component.element as HTMLElement).style.position = "absolute";
        (component.element as HTMLElement).style.zIndex = "9999";
        document.body.appendChild(component.element);
        updatePosition(props.editor, component.element as HTMLElement);
      },

      onUpdate(props: MentionProps) {
        component?.updateProps({ ...props, mode, query: props.query });

        const clientRect =
          typeof props.clientRect === "function"
            ? props.clientRect()
            : props.clientRect;

        if (!clientRect) {
          return;
        }

        updatePosition(props.editor, component!.element as HTMLElement);
      },

      onKeyDown(props: SuggestionKeyDownProps) {
        if (props.event?.key === "Escape") {
          component?.destroy();
          return true;
        }

        if (component?.ref && props.event) {
          return component.ref.onKeyDown({ event: props.event });
        }

        return false;
      },

      onExit() {
        component?.destroy();
      },
    };
  },
});

export default mentionSuggestion;
